import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_AUDIENCE_SCOPES,
  CAMPAIGN_SCOPES,
  CAMPAIGN_SOURCE_SCOPES,
  CAMPAIGN_TRIGGER_ORDER,
  CampaignModalChip,
  CampaignModalSection,
  CampaignOptionCard,
  CampaignSubChoice,
} from '#/entities/campaign'
import type { CampaignTrigger } from '#/entities/campaign'

import { MultiSelect } from '#/shared/ui/multi-select'

import {
  useCampaignSourceOptions,
  useCampaignUserOptions,
} from '../model/use-campaign-audience-options'
import type { UseCampaignEnrolmentResult } from '../model/use-campaign-enrolment'

/** "WHO IT BELONGS TO" — identical in both modals. */
export function CampaignScopeSection({
  state,
  /** The org card shows a "Runs for" label in one modal and bare chips in the
   *  other; the design differs, the meaning does not. */
  showRunsForLabel = false,
}: {
  state: UseCampaignEnrolmentResult
  showRunsForLabel?: boolean
}) {
  const { t } = useTranslation()
  const users = useCampaignUserOptions(state.picksUsers)
  return (
    <CampaignModalSection label={t('campaigns.modal.scope.label')}>
      <CampaignOptionCard
        title={t('campaigns.modal.scope.justMe.title')}
        description={t('campaigns.modal.scope.justMe.description')}
        selected={state.scope === CAMPAIGN_SCOPES.justMe}
        onSelect={() => state.setScope(CAMPAIGN_SCOPES.justMe)}
      />
      <CampaignOptionCard
        title={t('campaigns.modal.scope.organization.title')}
        description={t('campaigns.modal.scope.organization.description')}
        selected={state.isOrganization}
        onSelect={() => state.setScope(CAMPAIGN_SCOPES.organization)}
      >
        <CampaignSubChoice
          label={
            showRunsForLabel ? t('campaigns.modal.scope.runsFor') : undefined
          }
        >
          <CampaignModalChip
            label={t('campaigns.modal.scope.allUsers')}
            selected={state.audience === CAMPAIGN_AUDIENCE_SCOPES.allUsers}
            onClick={() => state.setAudience(CAMPAIGN_AUDIENCE_SCOPES.allUsers)}
          />
          <CampaignModalChip
            label={t('campaigns.modal.scope.pickUsers')}
            selected={state.audience === CAMPAIGN_AUDIENCE_SCOPES.pickUsers}
            onClick={() =>
              state.setAudience(CAMPAIGN_AUDIENCE_SCOPES.pickUsers)
            }
          />
        </CampaignSubChoice>
        {state.picksUsers && (
          <MultiSelect
            portal
            searchable
            options={users.options}
            loading={users.isLoading}
            value={state.userIds}
            onValueChange={state.setUserIds}
            placeholder={t('campaigns.modal.scope.pickUsersPlaceholder')}
            className="mt-2"
          />
        )}
      </CampaignOptionCard>
    </CampaignModalSection>
  )
}

/** The trigger + sources rows, shared by "A trigger fires" and "Keep adding". */
export function CampaignTriggerFields({
  state,
  /** Which chips to offer — the editor of a saved trigger campaign leaves
   *  out the one that is really a filter, since the mode cannot change. */
  triggers = CAMPAIGN_TRIGGER_ORDER,
  /** Sources only — a saved filter or manual campaign has no trigger to pick. */
  showTriggers = true,
}: {
  state: UseCampaignEnrolmentResult
  triggers?: readonly CampaignTrigger[]
  showTriggers?: boolean
}) {
  const { t } = useTranslation()
  const sourceOptions = useCampaignSourceOptions(state.picksSources)
  return (
    <>
      {showTriggers && (
        <CampaignSubChoice label={t('campaigns.modal.join.triggerLabel')}>
          {triggers.map((trigger) => (
            <CampaignModalChip
              key={trigger}
              label={t(`campaigns.modal.join.triggers.${trigger}`)}
              selected={state.trigger === trigger}
              onClick={() => state.setTrigger(trigger)}
            />
          ))}
        </CampaignSubChoice>
      )}
      <CampaignSubChoice label={t('campaigns.modal.join.sources')}>
        <CampaignModalChip
          label={t('campaigns.modal.join.allSources')}
          selected={state.sources === CAMPAIGN_SOURCE_SCOPES.allSources}
          onClick={() => state.setSources(CAMPAIGN_SOURCE_SCOPES.allSources)}
        />
        <CampaignModalChip
          label={t('campaigns.modal.join.pickSources')}
          selected={state.sources === CAMPAIGN_SOURCE_SCOPES.pickSources}
          onClick={() => state.setSources(CAMPAIGN_SOURCE_SCOPES.pickSources)}
        />
      </CampaignSubChoice>
      {state.picksSources && (
        <MultiSelect
          portal
          searchable
          options={sourceOptions.options}
          loading={sourceOptions.isLoading}
          value={state.providerIds}
          onValueChange={state.setProviderIds}
          placeholder={t('campaigns.modal.join.pickSourcesPlaceholder')}
        />
      )}
      <p className="text-xs leading-4 text-warm-500">
        {t('campaigns.modal.join.sourcesNote')}
      </p>
    </>
  )
}
